import React from 'react';
import PropTypes from 'prop-types';
import {injectIntl} from 'react-intl';
import {ICONS} from '../../lib/curo-icons.js';
import {TEMPLATES} from '../../lib/curo-templates.js';

/**
 * 시작 화면 — 빈 워크스페이스 대신 "무엇을 만들지" 고르고 시작.
 * 2026-07-23 activation(전략문서 1순위): Studio가 1등인데 빈 화면이라 첫 결과까지 오래 걸림.
 * ⭐ 자동저장(07-23)이 깔려 있어 "이어하기"를 여기서 함께 제안 → 수업 연속성.
 * 첫 방문 1회만(localStorage). 상단바 "새 기능"처럼 다시 열 수단은 두지 않음(방해 최소화) —
 * 대신 이어하기 배너는 autosave 컴포넌트가 계속 담당.
 */

const KEY = 'curoai_start_seen';

const MSG = {
    title: {en: 'What do you want to make?', ko: '무엇을 만들어 볼까요?', vi: 'Bạn muốn làm gì?'},
    sub: {en: 'Pick one to start — you can change anything later', ko: '하나 골라서 시작해요 · 나중에 얼마든지 바꿀 수 있어요', vi: 'Chọn một để bắt đầu'},
    skip: {en: 'Start empty', ko: '빈 화면으로 시작', vi: 'Bắt đầu trống'},
    resumeT: {en: 'Continue last time', ko: '지난 작업 이어하기', vi: 'Tiếp tục lần trước'},
    resumeD: {en: 'Open the project you were working on', ko: '하던 작품을 그대로 열어요', vi: 'Mở dự án bạn đang làm'},
    tpl: {
        blank: {
            n: {en: 'Empty project', ko: '빈 프로젝트', vi: 'Dự án trống'},
            d: {en: 'Start from scratch', ko: '처음부터 자유롭게', vi: 'Bắt đầu từ đầu'}
        },
        move: {
            n: {en: 'Move and talk', ko: '움직이고 말하기', vi: 'Di chuyển và nói'},
            d: {en: 'Blocks are ready — press the flag', ko: '블록이 준비돼 있어요 · 깃발을 눌러보세요', vi: 'Khối đã sẵn sàng — nhấn cờ'}
        },
        keys: {
            n: {en: 'Move with arrow keys', ko: '방향키로 움직이기', vi: 'Di chuyển bằng phím mũi tên'},
            d: {en: 'The basics of any game', ko: '게임 만들기의 기본이에요', vi: 'Nền tảng của mọi trò chơi'}
        },
        chase: {
            n: {en: 'Chase the mouse', ko: '마우스 따라다니기', vi: 'Đuổi theo chuột'},
            d: {en: 'It keeps following your cursor', ko: '마우스를 계속 쫓아다녀요', vi: 'Luôn đi theo con trỏ của bạn'}
        },
        ai: {
            n: {en: 'Train my own AI', ko: '나만의 AI 학습시키기', vi: 'Huấn luyện AI của tôi'},
            d: {en: 'Teach the webcam to recognize things', ko: '웹캠으로 사물을 알아보게 가르쳐요', vi: 'Dạy webcam nhận biết đồ vật'}
        },
        data: {
            n: {en: 'Charts and data', ko: '데이터로 차트 만들기', vi: 'Biểu đồ và dữ liệu'},
            d: {en: 'Collect numbers and draw graphs', ko: '숫자를 모아 그래프로 그려요', vi: 'Thu thập số liệu và vẽ biểu đồ'}
        },
        python: {
            n: {en: 'Code with Python', ko: '파이썬으로 코딩하기', vi: 'Lập trình Python'},
            d: {en: 'Write real Python in the browser', ko: '브라우저에서 진짜 파이썬을 써요', vi: 'Viết Python thật trên trình duyệt'}
        }
    }
};
const MSG_EXTRA = {
    ja: {title: '何を作ってみる？', sub: 'ひとつ選んで始めよう · あとから自由に変えられます', skip: '空の画面で始める', resumeT: '前回の続きから', resumeD: '作りかけの作品をそのまま開きます'},
    'zh-cn': {title: '想做点什么？', sub: '选一个开始 · 之后可以随意修改', skip: '从空白开始', resumeT: '继续上次的作品', resumeD: '直接打开你正在做的作品'},
    'zh-tw': {title: '想做點什麼？', sub: '選一個開始 · 之後可以隨意修改', skip: '從空白開始', resumeT: '繼續上次的作品', resumeD: '直接開啟你正在做的作品'},
    de: {title: 'Was möchtest du machen?', sub: 'Wähle etwas aus — du kannst alles später ändern', skip: 'Leer starten', resumeT: 'Weitermachen', resumeD: 'Öffnet dein letztes Projekt'},
    es: {title: '¿Qué quieres crear?', sub: 'Elige uno para empezar — puedes cambiarlo luego', skip: 'Empezar vacío', resumeT: 'Continuar lo anterior', resumeD: 'Abre el proyecto en el que trabajabas'},
    fr: {title: 'Que veux-tu créer ?', sub: 'Choisis pour commencer — tu peux tout changer ensuite', skip: 'Commencer vide', resumeT: 'Reprendre', resumeD: 'Ouvre ton dernier projet'},
    it: {title: 'Cosa vuoi creare?', sub: 'Scegline uno per iniziare — puoi cambiare tutto dopo', skip: 'Inizia vuoto', resumeT: 'Riprendi', resumeD: 'Apre il progetto a cui stavi lavorando'},
    ru: {title: 'Что хочешь создать?', sub: 'Выбери и начни — потом всё можно изменить', skip: 'Начать с пустого', resumeT: 'Продолжить', resumeD: 'Откроет проект, над которым вы работали'}
};

class StartScreen extends React.Component {
    constructor (props) {
        super(props);
        let seen = true;
        try { seen = !!localStorage.getItem(KEY); } catch (e) { seen = true; }
        this.state = {open: !seen};
        this.close = this.close.bind(this);
    }
    _mark () { try { localStorage.setItem(KEY, '1'); } catch (e) { /* noop */ } }
    close () { this._mark(); this.setState({open: false}); }
    pick (tpl) {
        this._mark();
        this.setState({open: false});
        if (this.props.onPick) {
            try { this.props.onPick(tpl); } catch (e) { /* noop */ } // 템플릿 적용 실패해도 화면은 닫힘
        }
    }
    t (key) {
        let loc = (this.props.intl && this.props.intl.locale) || 'en';
        if (loc === 'ja-Hira') loc = 'ja';
        const ex = MSG_EXTRA[loc];
        if (ex && ex[key] != null) return ex[key];
        const m = MSG[key];
        if (!m) return key;
        return m[loc] || m.en || key;
    }
    // 템플릿 이름/설명은 MSG.tpl 에서 (MSG_EXTRA 에 없으면 en 폴백)
    tt (tplKey, field) {
        let loc = (this.props.intl && this.props.intl.locale) || 'en';
        if (loc === 'ja-Hira') loc = 'ja';
        const m = MSG.tpl[tplKey];
        if (!m || !m[field]) return '';
        return m[field][loc] || m[field].en || '';
    }
    render () {
        if (!this.state.open) return null;
        const items = TEMPLATES.filter(t => t.key !== 'blank');
        return (
            <div style={S.overlay}>
                <div style={S.modal}>
                    <div style={S.title}>{this.t('title')}</div>
                    <div style={S.sub}>{this.t('sub')}</div>
                    <div style={S.grid}>
                        {items.map(tpl => (
                            <button
                                key={tpl.key}
                                style={S.card}
                                onClick={() => this.pick(tpl)}
                            >
                                <span
                                    style={Object.assign({}, S.icon, {background: tpl.color})}
                                    dangerouslySetInnerHTML={{__html: ICONS[tpl.icon] || ''}}
                                />
                                <span style={S.name}>{this.tt(tpl.key, 'n')}</span>
                                <span style={S.desc}>{this.tt(tpl.key, 'd')}</span>
                            </button>
                        ))}
                    </div>
                    <div style={S.footer}>
                        <button style={S.skip} onClick={this.close}>{this.t('skip')}</button>
                    </div>
                </div>
            </div>
        );
    }
}

const S = {
    overlay: {position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(20,24,40,0.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9500},
    modal: {background: '#fff', borderRadius: 16, width: 'min(680px, 94vw)', maxHeight: '90vh', overflow: 'auto',
        padding: '26px 26px 18px', boxShadow: '0 16px 48px rgba(0,0,0,0.3)',
        fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif', textAlign: 'center'},
    title: {fontSize: 22, fontWeight: 'bold', color: '#1f2540'},
    sub: {fontSize: 13, color: '#67708a', marginTop: 6},
    grid: {display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginTop: 20},
    card: {display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, padding: '18px 14px',
        border: '1px solid #e6e8f0', borderRadius: 14, background: '#fff', cursor: 'pointer', textAlign: 'center'},
    icon: {width: 46, height: 46, borderRadius: '50%', color: '#fff', fontSize: 22,
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flex: 'none'},
    name: {fontSize: 14, fontWeight: 'bold', color: '#1f2540'},
    desc: {fontSize: 12, color: '#67708a', lineHeight: 1.4},
    footer: {marginTop: 18},
    skip: {border: 'none', background: 'transparent', color: '#8a90a3', fontSize: 13, cursor: 'pointer',
        textDecoration: 'underline'}
};

StartScreen.propTypes = {
    intl: PropTypes.object,
    onPick: PropTypes.func
};

export default injectIntl(StartScreen);
